import { useMemo } from "react";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { useCachedQuery } from "./useCachedQuery";
import type { AnalyticsEventType } from "./useAnalytics";

export type AdminAnalyticsEvent = {
  id: number;
  userEmail: string;
  type: AnalyticsEventType;
  subject: string | null;
  note: string | null;
  createdAt: string;
};

export type AdminEventsFilter = {
  user?: string | undefined;
  types?: ReadonlyArray<AnalyticsEventType> | undefined;
};

type Status = "loading" | "ready" | "error";

type Result = {
  status: Status;
  events: ReadonlyArray<AdminAnalyticsEvent>;
  error: string | null;
  isFetching: boolean;
  refresh: () => void;
};

const PERSIST_CAP = 100;

const buildUrl = (filter: AdminEventsFilter): string => {
  const params = new URLSearchParams();
  params.set("limit", "500");
  if (filter.user && filter.user.trim().length > 0) {
    params.set("user", filter.user.trim().toLowerCase());
  }
  if (filter.types && filter.types.length > 0) {
    params.set("types", filter.types.join(","));
  }
  return `/api/admin/events?${params.toString()}`;
};

export const useAdminEvents = (filter: AdminEventsFilter): Result => {
  // Snapshot the filter inputs we depend on to avoid effect churn when the
  // caller passes a fresh array literal every render.
  const userKey = filter.user?.trim() ?? "";
  const typesKey = filter.types?.join(",") ?? "";
  const url = useMemo(
    () => buildUrl({ user: userKey || undefined, types: filter.types }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userKey, typesKey],
  );
  const isDefault = userKey === "" && typesKey === "";

  const fetcher = useMemo(
    () => async (signal: AbortSignal): Promise<ReadonlyArray<AdminAnalyticsEvent>> => {
      const res = await fetch(url, { credentials: "include", signal });
      if (!res.ok) throw new Error(`fetch failed (${res.status})`);
      return (await res.json()) as ReadonlyArray<AdminAnalyticsEvent>;
    },
    [url],
  );

  const { status, data, error, isFetching, refetch } = useCachedQuery<
    ReadonlyArray<AdminAnalyticsEvent>
  >({
    cacheResource: isDefault ? ADMIN_QUERIES.events.resource : null,
    queryKey: url,
    fetcher,
    persistTransform: (events) => events.slice(0, PERSIST_CAP),
  });

  return {
    status,
    events: data ?? [],
    error,
    isFetching,
    refresh: () => void refetch(),
  };
};
